import { useEffect, useMemo, useState } from "react";
import "./DesktopLogin.css";
import { authApi } from "../../../api";
import { useLang } from "../../../i18n/LangContext";

const FALLBACK = {
  eyebrow: "RESET PASSWORD",
  title: "Set a new password",
  subtitle: "Enter and confirm your new password.",
  passwordLabel: "New password",
  passwordPlaceholder: "Enter a new password",
  confirmLabel: "Confirm password",
  confirmPlaceholder: "Repeat your new password",
  submit: "Update password",
  success: "Password updated successfully.",
  error: "Unable to reset password.",
  missingToken: "Missing reset token.",
  mismatch: "Passwords do not match.",
};

export default function DesktopResetPassword({ onSuccess = () => {} }) {
  const { t } = useLang();
  const copy = useMemo(() => t.resetPassword || FALLBACK, [t.resetPassword]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPassword("");
    setConfirm("");
    setStatus("");
    setError("");
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setStatus("");

    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (!token) {
      setError(copy.missingToken || FALLBACK.missingToken);
      return;
    }

    if (!password || !confirm) {
      setError(copy.error || FALLBACK.error);
      return;
    }
    if (password !== confirm) {
      setError(copy.mismatch || FALLBACK.mismatch);
      return;
    }

    try {
      setLoading(true);
      await authApi.resetPassword({ token, newPassword: password });
      setStatus(copy.success || FALLBACK.success);
      onSuccess();
    } catch (err) {
      const message =
        err?.data?.statusText ||
        err?.data?.statusDesc ||
        err?.message ||
        copy.error ||
        FALLBACK.error;
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="desktop-login-overlay" role="dialog" aria-modal="true">
      <div className="desktop-login-card">
        <p className="desktop-login-eyebrow">{copy.eyebrow}</p>
        <h2 className="desktop-login-title">{copy.title}</h2>
        <p className="desktop-login-subtitle">{copy.subtitle}</p>

        <form className="desktop-login-form" onSubmit={handleSubmit}>
          <label className="desktop-login-field">
            <span>{copy.passwordLabel}</span>
            <input
              type="password"
              name="password"
              placeholder={copy.passwordPlaceholder}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </label>
          <label className="desktop-login-field">
            <span>{copy.confirmLabel}</span>
            <input
              type="password"
              name="confirm"
              placeholder={copy.confirmPlaceholder}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
            />
          </label>

          {(error || status) && (
            <div className={`desktop-login-status ${error ? "is-error" : "is-success"}`}>
              {error || status}
            </div>
          )}

          <button type="submit" className="desktop-login-submit" disabled={loading}>
            <span>{loading ? "..." : (copy.submit || FALLBACK.submit).toUpperCase()}</span>
          </button>
        </form>
      </div>
    </div>
  );
}
